import { useEffect, useRef, useState } from 'react'
import { environment, urls } from 'Config'

import { useFeatureFlags } from '@/providers/featureFlags/FeatureFlagsProvider'
import useFeatureFlagsReady from '@/hooks/useFeatureFlagsReady'
import { FEATURE_FLAG_KEYS } from '@/constants'
import { checkMarketingPage } from '@/api/marketing'

const env = (() => {
  switch (true) {
    case environment.startsWith('sandbox'):
      return 'sandbox'
    case environment.startsWith('production'):
      return 'production'
    default:
      return 'test'
  }
})()

// The flag value ends up as an iframe `src`, so only an absolute https URL is accepted -
// anything else (typo, `javascript:` URL) falls back to the build-time base URL.
const isHttpsUrl = value => {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

// Past this, a page whose host has not answered (and that has not loaded) counts as unavailable.
export const LOAD_TIMEOUT = 10000

const MarketingIframe = () => {
  const featureFlags = useFeatureFlags()
  const isFlagsReady = useFeatureFlagsReady()
  const flagUrl = featureFlags[FEATURE_FLAG_KEYS.LOGIN_MARKETING_IFRAME_URL]
  const baseUrl = isHttpsUrl(flagUrl) ? flagUrl : urls.marketingIframe
  const src = `${baseUrl}${env}`

  const [failed, setFailed] = useState(() => !navigator.onLine)
  const loaded = useRef(false)

  // A cross-origin iframe fires `load` even when it shows the browser's own error page, so whether the
  // page is reachable (offline, DNS, blocked) is checked apart from it. `load` also waits for every image
  // and script of the page, so a host that answered is never timed out: its page may just be slow.
  useEffect(() => {
    if (!isFlagsReady || failed) return undefined

    let active = true
    let answered = false
    loaded.current = false
    const fail = () => { if (active) setFailed(true) }
    checkMarketingPage(src).then(() => { answered = true }, fail)
    const timer = setTimeout(() => { if (!answered && !loaded.current) fail() }, LOAD_TIMEOUT)

    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [failed, isFlagsReady, src])

  // Held back until the flag has resolved, so the iframe does not load the fallback URL
  // first and then reload with the flag value.
  return (
    <div className="login-iframe">
      {isFlagsReady && (failed ? (
        <div className="marketing-fallback" data-testid="marketing-fallback" aria-hidden="true" />
      ) : (
        <iframe
          className="responsive-iframe"
          title="ZenQMS customer resources"
          src={src}
          onLoad={() => { loaded.current = true }}
        ></iframe>
      ))}
    </div>
  )
}

export default MarketingIframe
